import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch } from "react-redux";

import api from "../../../api";
import { AdvertiserType } from "../../../types/advertiserTypes";
import { generateBrandgenAdvertiserUuid } from "../../../utils/generateBrandgenUuid";
import { infoToaster } from "../../../helpers/toasters";
import { setAgencyAdvertisers } from "../../../redux/reducers/agency";
import { useCurrentAuthenticatedUser } from "../../../hooks/useCurrentAuthenticatedUser";

type CreateAdvertiserModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

type FormInitialValues = {
  name: string;
  phoneNumber: string;
  firstAddress: string;
  secondAddress: string;
  city: string;
  state: string;
  postalCode: string;
  domain: string;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Required field."),
  phoneNumber: Yup.string().required("Required field."),
  firstAddress: Yup.string().required("Required field."),
  city: Yup.string().required("Required field."),
  state: Yup.string().required("Required field."),
  postalCode: Yup.string().required("Required field."),
  domain: Yup.string()
    .required("Required field.")
    .matches(
      /^(?:ht{2}ps?:\/\/)?(?:[\dA-Za-z-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:#\S*)?$/,
      "Must be a valid domain without path",
    ),
});

const createAdvertiser = async (data: AdvertiserType) => {
  try {
    await api.createAdvertiser(data);
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const ModalHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "20px 24px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const ModalTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  padding: "24px !important",
  minWidth: 600,
  maxWidth: 720,
}));

const FieldGridTwoCols = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
});

const FieldGridThreeCols = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 12,
});

const FieldWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  marginBottom: 8,
});

const StyledTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
    },
  },
  "& .MuiInputBase-root": {
    fontSize: 13,
  },
  "& .MuiInputLabel-outlined": {
    fontSize: 13,
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 12px",
  },
}));

const ErrorText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.errorRed,
  minHeight: 30,
  marginTop: 4,
  display: "block",
}));

const FormErrorBanner = styled("div")(({ theme }: { theme: Theme }) => ({
  marginTop: 8,
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${theme.palette.brand.gray200}`,
  background: theme.palette.brand.white,
  color: theme.palette.brand.errorRed,
  fontSize: 13,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray700,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderColor: theme.palette.brand.gray300,
  borderRadius: 8,
  padding: "8px 14px",
  "&:hover": {
    background: theme.palette.brand.gray50,
    borderColor: theme.palette.brand.gray300,
  },
}));

const SubmitButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 8,
  padding: "8px 16px",
  minWidth: 110,
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "none",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: theme.palette.brand.gray200,
    color: theme.palette.brand.gray500,
  },
}));

const CreateAdvertiserModalRedesign: FC<CreateAdvertiserModalProps> = ({ isOpen, handleClose }) => {
  const dispatch = useDispatch();
  const { currentUsername, agencyId } = useCurrentAuthenticatedUser();

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const formInitialValues: FormInitialValues = {
    name: "",
    phoneNumber: "",
    firstAddress: "",
    secondAddress: "",
    city: "",
    state: "",
    postalCode: "",
    domain: "",
  };

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      const { name, phoneNumber, firstAddress, secondAddress, city, state, postalCode, domain } = values;
      const newBrandgenAdvertiserId = generateBrandgenAdvertiserUuid();
      const advertiserToCreate: Partial<AdvertiserType> = {
        name,
        phone: phoneNumber,
        address_1: firstAddress,
        address_2: secondAddress,
        city,
        state,
        zip: postalCode,
        domain,
        brandgenAdvertiserId: newBrandgenAdvertiserId,
        createdBy: currentUsername,
        parentAgencyId: agencyId,
      };

      await createAdvertiser(advertiserToCreate as AdvertiserType);
      dispatch(setAgencyAdvertisers(agencyId));
      infoToaster("The selected advertising must have a connected payment method to configure a campaign!", 2000);

      setIsLoading(false);
      handleClose();
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : handleClose}
      aria-labelledby="create-advertiser-modal-title"
      maxWidth="md"
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values }) => (
          <>
            <ModalHeader>
              <ModalTitle id="create-advertiser-modal-title">Create New Advertiser</ModalTitle>
            </ModalHeader>

            <Form>
              <StyledDialogContent>
                <FieldGridTwoCols>
                  <FieldWrapper>
                    <Field
                      as={StyledTextField}
                      name="name"
                      label="Name *"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.name}
                      error={!!errorMessage}
                    />
                    <ErrorText>
                      <ErrorMessage name="name" />
                    </ErrorText>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Field
                      as={StyledTextField}
                      name="phoneNumber"
                      label="Phone Number *"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.phoneNumber}
                      error={!!errorMessage}
                    />
                    <ErrorText>
                      <ErrorMessage name="phoneNumber" />
                    </ErrorText>
                  </FieldWrapper>
                </FieldGridTwoCols>

                <FieldWrapper>
                  <Field
                    as={StyledTextField}
                    name="firstAddress"
                    label="Address *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.firstAddress}
                    error={!!errorMessage}
                  />
                  <ErrorText>
                    <ErrorMessage name="firstAddress" />
                  </ErrorText>
                </FieldWrapper>

                <FieldWrapper>
                  <Field
                    as={StyledTextField}
                    name="secondAddress"
                    label="Address 2"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.secondAddress}
                  />
                  <ErrorText />
                </FieldWrapper>

                <FieldGridThreeCols>
                  <FieldWrapper>
                    <Field
                      as={StyledTextField}
                      name="city"
                      label="City *"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.city}
                      error={!!errorMessage}
                    />
                    <ErrorText>
                      <ErrorMessage name="city" />
                    </ErrorText>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Field
                      as={StyledTextField}
                      name="state"
                      label="State *"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.state}
                      error={!!errorMessage}
                    />
                    <ErrorText>
                      <ErrorMessage name="state" />
                    </ErrorText>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Field
                      as={StyledTextField}
                      name="postalCode"
                      label="Postal Code *"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.postalCode}
                      error={!!errorMessage}
                    />
                    <ErrorText>
                      <ErrorMessage name="postalCode" />
                    </ErrorText>
                  </FieldWrapper>
                </FieldGridThreeCols>

                <FieldWrapper>
                  <Field
                    as={StyledTextField}
                    name="domain"
                    label="Website *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.domain}
                    error={!!errorMessage}
                  />
                  <ErrorText>
                    <ErrorMessage name="domain" />
                  </ErrorText>
                </FieldWrapper>

                {errorMessage && <FormErrorBanner>{errorMessage}</FormErrorBanner>}
              </StyledDialogContent>

              <StyledDialogActions>
                <CancelButton variant="outlined" onClick={isLoading ? undefined : handleClose} disabled={isLoading}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" variant="contained" disabled={isLoading}>
                  {isLoading ? <CircularProgress size={20} style={{ color: "inherit" }} /> : "Submit"}
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateAdvertiserModalRedesign;
